import React, { Component } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    MenuItem,
    Select,
    SelectChangeEvent,
    Tabs,
    TextField,
    Typography,
    IconButton,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { faSave } from '@fortawesome/pro-duotone-svg-icons/faSave'
import { faTimes } from '@fortawesome/pro-duotone-svg-icons/faTimes'
import { faExclamationCircle } from '@fortawesome/pro-duotone-svg-icons/faExclamationCircle'

import LoadingDefault from '@/Components/Elements/Loading/LoadingDefault'
import LoadingContent from '@/Components/Elements/Loading/LoadingContent'
import Applying from '@/Components/Admin/Pages/Templates/Applying'
import DefaultSearchbar from '@/Components/Elements/Search/DefaultSearchbar'
import Pagination from '@/Components/Elements/Pagination'
import logo from '../../../images/xlab-logo.jpg'
import ItemCounter from '../../../Components/Elements/ItemCounter'
import ConfirmDeleteChangesDialog from '@/Components/Form/ConfirmDeleteChangesDialog'
import AppDialog from '@/Components/AppDialog/AppDialog'
import { StyledButton } from '@/Components/Elements/StyledComponents/Buttons'
import TabPanel from '@/Components/Elements/TabPanel/TabPanel'
import StyledVerticalTab from '@/Components/Elements/StyledComponents/Tabs/StyledVerticalTab'

interface NewEnvironmentProps {
    kedo: any
    onClose: () => void
}

interface Template {
    id: number | string
    name: string
    description: string
    created_at: string
    thumbnail_path?: string
    translations?: Array<{
        culture: string
        thumbnail_path: string
    }>
    template?: string
}

interface Locale {
    id: number | string
    code: string
    name: string
}

interface TemplatePager {
    pages: number
    current: number
    limit: number
    items: number
}

interface NewEnvironmentState {
    addLocales: Locale[]
    description: string | null
    file: File | string
    imagePreviewUrl: string
    name: string | null
    template: number | string | null

    activeTab: 'generic' | 'languages' | 'templates' | 'misc'
    errorFields: Record<string, any>
    errors: Record<string, string> | false
    limit: number
    locales: Locale[]
    loadingLocales: boolean
    runTemplate: boolean
    templates: Template[]
    NewEnvironmentForm: boolean
    saving: boolean
    searchTemplate: string | null
    page: number
    unsavedChanges: boolean
    fetchingTemplates?: boolean
    templatePages?: TemplatePager
    showConfirmChanges?: boolean
    general?: string | null
    timeoutTemplate?: NodeJS.Timeout
    newEnvironment?: any
}

class NewEnvironment extends Component<
    NewEnvironmentProps,
    NewEnvironmentState
> {
    _isMounted = false
    innerRef: React.RefObject<HTMLInputElement>

    constructor(props: NewEnvironmentProps) {
        super(props)

        this.innerRef = React.createRef<HTMLInputElement>()

        this.state = {
            addLocales: [],
            description: null,
            file: '',
            imagePreviewUrl: '',
            name: null,
            template: null,

            activeTab: 'generic',
            errorFields: {},
            errors: false,
            limit: 9,
            locales: [],
            loadingLocales: false,
            runTemplate: false,
            templates: [],
            NewEnvironmentForm: false,
            saving: false,
            searchTemplate: null,
            page: 0,
            unsavedChanges: false,
        }

        this._handleImageChange = this._handleImageChange.bind(this)
        this.handleChangeTemplates = this.handleChangeTemplates.bind(this)
        this.handleClose = this.handleClose.bind(this)
        this.handleCloseModal = this.handleCloseModal.bind(this)
        this.handleEntriesChange = this.handleEntriesChange.bind(this)
        this.handleSearchTemplates = this.handleSearchTemplates.bind(this)
        this.onKeyUpTemplate = this.onKeyUpTemplate.bind(this)
        this.handleExit = this.handleExit.bind(this)
        this.handleFieldChange = this.handleFieldChange.bind(this)
        this.handleTemplateChange = this.handleTemplateChange.bind(this)
        this.onChangePage = this.onChangePage.bind(this)
        this.resetButton = this.resetButton.bind(this)
        this.handleReset = this.handleReset.bind(this)
        this.save = this.save.bind(this)
        this.renderApply = this.renderApply.bind(this)
    }

    fetchTemplates() {
        if (this._isMounted !== true) {
            return
        }

        const params = {
            params: {
                limit: this.state.limit,
                page: this.state.page,
            },
        }
        const url = this.props.kedo.api().getAdminTemplateEndpoint()

        if (this.state.searchTemplate && this.state.searchTemplate.length > 0) {
            params.params = {
                ...params.params,
                name: this.state.searchTemplate,
            }
        }

        this.setState({ fetchingTemplates: true })
        this.props.kedo
            .api()
            .get(url, params)
            .then((response: any) =>
                this.setState({
                    templates: response.data.results,
                    templatePages: response.data.pager,
                    fetchingTemplates: false,
                })
            )
    }

    fetchLocales() {
        if (this._isMounted !== true) {
            return
        }
        this.setState({ loadingLocales: true })
        this.props.kedo
            .api()
            .get(this.props.kedo.api().getLocaleEndpoint())
            .then((response: any) =>
                this.setState({
                    loadingLocales: false,
                    locales: response.data.results,
                })
            )
            .catch((error: any) => this.setState({ loadingLocales: false }))
    }

    handleChangeTemplates(event: React.ChangeEvent<HTMLInputElement>) {
        this.setState({
            searchTemplate: event.target.value,
        })
    }

    handleSearchTemplates(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.key !== 'Enter') {
            return
        }

        this.setState(
            { searchTemplate: (event.target as HTMLInputElement).value },
            this.fetchTemplates
        )
    }

    handleCheck(event: React.ChangeEvent<HTMLInputElement>, addItem: Locale) {
        const items = this.state.addLocales.filter(
            (item) => item.id !== addItem.id
        )
        if (event.target.checked) {
            items.push(addItem)
        }

        this.setState({ addLocales: items })
    }

    checkErrors() {
        if (!this.state.errors) {
            return ''
        }

        if (this.innerRef.current && this.innerRef.current.focus) {
            setTimeout(() => this.innerRef.current?.focus(), 1)
        }

        if (this.state.errors.name) {
            return 'name'
        } else if (this.state.errors.description) {
            return 'description'
        } else if (this.state.errors.language) {
            return 'language'
        }
    }

    handleClose() {
        this.props.onClose()
    }

    handleReset() {
        this.setState(
            { page: 0, searchTemplate: '', templates: [] },
            this.fetchTemplates
        )
    }

    resetButton() {
        return (
            <IconButton
                onClick={this.handleReset}
                className="resetButton"
                size="small"
                sx={{ ml: 0.5 }}
            >
                <FontAwesomeIcon icon={faTimes} />
            </IconButton>
        )
    }

    onChangePage(number: number) {
        this.setState({ page: number }, this.fetchTemplates)
    }

    handleErrors(errorData: any) {
        //TODO: Create handling
    }

    handleExit() {
        this.setState({
            NewEnvironmentForm: false,
            saving: false,
            file: '',
        })
        this.props.onClose()
    }

    handleFieldChange(
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) {
        this.setState({
            [event.target.name]: event.target.value,
        } as Pick<NewEnvironmentState, keyof NewEnvironmentState>)
    }

    save() {
        if (
            !this.state.name ||
            !this.state.description ||
            this.state.addLocales.length <= 0
        ) {
            const errors: Record<string, string> = {}
            if (!this.state.name || this.state.name.length <= 0) {
                errors.name = this.props.kedo.t('This is a required field')
            }
            if (!this.state.description || this.state.description.length <= 0) {
                errors.description = this.props.kedo.t(
                    'This is a required field'
                )
            }
            if (!this.state.addLocales || this.state.addLocales.length <= 0) {
                errors.language = this.props.kedo.t('This is a required field')
            }

            this.setState({ errors: errors }, this.checkErrors)

            return
        }

        this.setState({
            saving: true,
            errors: false,
        })

        const environment = {
            name: this.state.name,
            description: this.state.description,
            image: this.state.imagePreviewUrl,
            template: this.state.template,
        }

        this.props.kedo
            .api()
            .post(this.props.kedo.api().getEnvironmentEndpoint(), environment)
            .then((response: any) => {
                this.handleOnPostEnvironmentCreate(response.data.id)
            })
            .catch((error: any) => {
                this.handleErrors(error.response)
                this.setState({
                    general:
                        error.response && error.response.status === 403
                            ? 'You cannot create any more environments in your subscription.'
                            : null,
                    errors: error.response.data.errors,
                    imagePreviewUrl: '',
                    name: null,
                    saving: false,
                })
            })
    }

    handleOnPostEnvironmentCreate(envId: number | string) {
        const total = this.state.addLocales.length
        this.state.addLocales.map((localeItem, index) => {
            const item = {
                environment: envId,
                code: localeItem.code,
            }

            this.props.kedo
                .api()
                .post(
                    this.props.kedo.api().getEnvironmentLocaleEndpoint(),
                    item
                )
                .then((successResponse: any) => {
                    // eslint-disable-next-line
                    if ((index) => total) {
                        this.handleExit()
                    }
                })
        })
    }

    _handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
        e.preventDefault()

        const file = e.target.files?.[0]
        if (!file) return

        const reader = new FileReader()

        reader.onloadend = () => {
            this.setState({
                file: file,
                imagePreviewUrl: reader.result as string,
            })
        }

        reader.readAsDataURL(file)
    }

    handleTemplateChange(itemId: number | string) {
        if (itemId === this.state.template) {
            this.setState({ template: null })
        } else {
            this.setState({ template: itemId })
        }
    }

    getLogo(item: Template) {
        let template: any = []

        if (
            this.props.kedo.env().locale &&
            (!item.translations || item.translations.length)
        ) {
            const currentLocale = this.props.kedo.env().getSelectedLocale()
            template = item.translations?.find(
                (item) => item.culture === currentLocale.code
            )
        }

        if (
            !this.props.kedo.env().locale ||
            !template ||
            template.length <= 0
        ) {
            return logo
        } else {
            const apiBaseUrl = this.props.kedo.api().getBaseUrl()

            return apiBaseUrl + '/' + template.thumbnail_path
        }
    }

    onKeyUpTemplate(event: React.KeyboardEvent) {
        if (this.state.timeoutTemplate) clearTimeout(this.state.timeoutTemplate)
        // eslint-disable-next-line react/no-direct-mutation-state
        this.state.timeoutTemplate = setTimeout(() => {
            this.fetchTemplates()
        }, 300)
    }

    handleEntriesChange(event: SelectChangeEvent<number>) {
        this.setState(
            { limit: Number(event.target.value) },
            this.fetchTemplates
        )
    }

    showEntries() {
        let limit = parseInt(String(this.state.limit))
        limit = limit < 0 || limit > 200 ? 50 : limit

        return (
            <Box className="show-entries" sx={{ float: 'right' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                        name="limit"
                        value={limit}
                        onChange={this.handleEntriesChange}
                    >
                        <MenuItem value={9}>9</MenuItem>
                        <MenuItem value={18}>18</MenuItem>
                        <MenuItem value={27}>27</MenuItem>
                        <MenuItem value={51}>51</MenuItem>
                        <MenuItem value={102}>102</MenuItem>
                        <MenuItem value={153}>153</MenuItem>
                        <MenuItem value={204}>204</MenuItem>
                    </Select>
                </FormControl>
            </Box>
        )
    }

    renderTemplates() {
        return this.state.fetchingTemplates ? (
            <LoadingContent />
        ) : (
            <div>
                <DefaultSearchbar
                    kedo={this.props.kedo}
                    handleChange={this.handleChangeTemplates}
                    handleSearch={this.handleSearchTemplates}
                    searchName={this.state.searchTemplate}
                    resetButton={this.resetButton}
                />
                {this.state.templates.length <= 0 ? (
                    <Alert severity="info" variant="outlined">
                        {this.props.kedo.t('No items found')}
                    </Alert>
                ) : (
                    <div>
                        <Grid container sx={{ mt: '-10px' }}>
                            <Grid size={{ xs: 12, md: 10 }} offset={{ md: 2 }}>
                                {this.showEntries()}
                            </Grid>
                        </Grid>
                        <br />
                        <Grid container spacing={2}>
                            {this.state.templates.map((item) => (
                                <Grid
                                    key={item.id}
                                    size={{ xs: 12, sm: 6, md: 4 }}
                                    sx={{ pb: '25px' }}
                                >
                                    <Card
                                        onClick={() =>
                                            this.handleTemplateChange(item.id)
                                        }
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            cursor: 'pointer',
                                            border:
                                                this.state.template === item.id
                                                    ? 2
                                                    : 1,
                                            borderColor:
                                                this.state.template === item.id
                                                    ? 'primary.main'
                                                    : 'divider',
                                            '&:hover': { boxShadow: 3 },
                                        }}
                                        className={
                                            this.state.template === item.id
                                                ? 'activeCard'
                                                : ''
                                        }
                                    >
                                        <Box
                                            className="template-image"
                                            sx={{ p: 2 }}
                                        >
                                            <CardMedia
                                                component="img"
                                                image={this.getLogo(item)}
                                                alt={item.name}
                                                sx={{ objectFit: 'contain' }}
                                            />
                                        </Box>
                                        <CardContent sx={{ flexGrow: 1 }}>
                                            <Typography
                                                variant="h6"
                                                gutterBottom
                                            >
                                                {item.name}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                                sx={{ mb: 1 }}
                                            >
                                                {this.props.kedo
                                                    .utils()
                                                    .dateFormat(
                                                        item.created_at
                                                    )}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {item.description}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                        {this.state.templatePages ? (
                            <div>
                                <Pagination
                                    pager={this.state.templatePages}
                                    onChangePage={this.onChangePage}
                                />
                                <ItemCounter pager={this.state.templatePages} />
                            </div>
                        ) : null}
                    </div>
                )}
            </div>
        )
    }

    handleCloseModal(changes: boolean) {
        if (changes) {
            this.setState({ showConfirmChanges: true })
            return this.renderRemoveChangesForm()
        }

        return this.props.onClose()
    }

    renderRemoveChangesForm() {
        if (this.state.showConfirmChanges) {
            return (
                <ConfirmDeleteChangesDialog
                    kedo={this.props.kedo}
                    variant={'modal'}
                    accept={this.handleClose}
                    cancel={() => this.setState({ showConfirmChanges: false })}
                />
            )
        }
    }

    newEnvironmentForm() {
        const kedo = this.props.kedo

        return (
            <AppDialog
                aria-labelledby="contained-modal-title-vcenter"
                open
                maxWidth={'lg'}
                fullWidth
                title={kedo.t('New environment')}
                onClose={() => this.handleCloseModal(this.state.unsavedChanges)}
                headerClose={() =>
                    this.handleCloseModal(this.state.unsavedChanges)
                }
                contentProps={{ dividers: true }}
                actions={
                    <>
                        <StyledButton onClick={this.save}>
                            {this.state.saving ? (
                                <LoadingDefault size={'sm'} />
                            ) : (
                                <FontAwesomeIcon icon={faSave} />
                            )}
                            &nbsp; {kedo.t('Save')}
                        </StyledButton>
                        <StyledButton
                            onClick={() => this.handleClose()}
                            color="secondary"
                            title={'Cancel'}
                        >
                            {kedo.t('Cancel')}
                        </StyledButton>
                    </>
                }
            >
                <div className={'pillsModalBody'}>
                    {this.state.general ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {this.state.general}
                        </Alert>
                    ) : null}
                    <Grid container>
                        <Grid size={3} className={'pillsSidebar'}>
                            <Tabs
                                value={this.state.activeTab}
                                onChange={(e, value) =>
                                    this.setState({
                                        activeTab: value as
                                            | 'generic'
                                            | 'languages'
                                            | 'templates'
                                            | 'misc',
                                    })
                                }
                                orientation="vertical"
                                sx={{
                                    '& .MuiTabs-indicator': { display: 'none' },
                                }}
                            >
                                <StyledVerticalTab
                                    label={
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                            }}
                                        >
                                            {kedo.t('Generic')}
                                            {this.state.errors &&
                                            (this.state.errors.name ||
                                                this.state.errors
                                                    .description) ? (
                                                <FontAwesomeIcon
                                                    className={`validationIcon validationFailed`}
                                                    icon={faExclamationCircle}
                                                />
                                            ) : null}
                                        </Box>
                                    }
                                    value="generic"
                                />
                                <StyledVerticalTab
                                    label={
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                            }}
                                        >
                                            {kedo.t('Languages')}
                                            {this.state.errors &&
                                            this.state.errors.language ? (
                                                <FontAwesomeIcon
                                                    className={`validationIcon validationFailed`}
                                                    icon={faExclamationCircle}
                                                />
                                            ) : null}
                                        </Box>
                                    }
                                    value="languages"
                                />
                                <StyledVerticalTab
                                    label={kedo.t('Templates')}
                                    value="templates"
                                />
                                <StyledVerticalTab
                                    label={kedo.t('Misc')}
                                    value="misc"
                                />
                            </Tabs>
                        </Grid>
                        <Grid size={9} className={'pillsContent'}>
                            <TabPanel
                                value={this.state.activeTab}
                                index="generic"
                            >
                                <Typography variant="h5" gutterBottom>
                                    {kedo.t('Generic')}
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={kedo.t('Name')}
                                    name="name"
                                    value={this.state.name || ''}
                                    onChange={this.handleFieldChange}
                                    inputRef={
                                        this.checkErrors() === 'name'
                                            ? this.innerRef
                                            : undefined
                                    }
                                    error={Boolean(
                                        this.state.errors &&
                                            this.state.errors.name
                                    )}
                                    helperText={
                                        this.state.errors &&
                                        this.state.errors.name
                                    }
                                    disabled={this.state.saving}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={kedo.t('Description')}
                                    name="description"
                                    value={this.state.description || ''}
                                    onChange={this.handleFieldChange}
                                    multiline
                                    rows={3}
                                    error={Boolean(
                                        this.state.errors &&
                                            this.state.errors.description
                                    )}
                                    helperText={
                                        this.state.errors &&
                                        this.state.errors.description
                                    }
                                    disabled={this.state.saving}
                                    sx={{ mb: 2 }}
                                />
                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mb: 1 }}
                                    >
                                        {kedo.t('Image')}
                                    </Typography>
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        size="small"
                                    >
                                        {kedo.t('Choose File')}
                                        <input
                                            type="file"
                                            accept="image/png, image/jpeg"
                                            hidden
                                            onChange={this._handleImageChange}
                                        />
                                    </Button>
                                    {this.state.imagePreviewUrl && (
                                        <Box sx={{ mt: 2 }}>
                                            <img
                                                src={this.state.imagePreviewUrl}
                                                alt="Preview"
                                                style={{
                                                    maxWidth: '200px',
                                                    maxHeight: '200px',
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </TabPanel>
                            <TabPanel
                                value={this.state.activeTab}
                                index="languages"
                            >
                                <Typography variant="h5" gutterBottom>
                                    {kedo.t('Languages')}
                                </Typography>
                                <FormControl
                                    error={Boolean(
                                        this.state.errors &&
                                            this.state.errors.language
                                    )}
                                    sx={{ width: '100%' }}
                                >
                                    <Box
                                        component="ul"
                                        className="list-group"
                                        sx={{ listStyle: 'none', p: 0 }}
                                    >
                                        {this.state.loadingLocales ? (
                                            <Box
                                                component="li"
                                                className="list-group-item"
                                            >
                                                <LoadingDefault />
                                                &nbsp; {kedo.t('Loading')}...
                                            </Box>
                                        ) : null}
                                        {this.state.locales.map((item) => (
                                            <Box
                                                key={item.id}
                                                component="li"
                                                className="list-group-item"
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={this.state.addLocales.some(
                                                                (loc) =>
                                                                    loc.id ===
                                                                    item.id
                                                            )}
                                                            onChange={(event) =>
                                                                this.handleCheck(
                                                                    event,
                                                                    item
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label={item.name}
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                    <FormHelperText>
                                        {kedo.t('Select at least 1 language')}
                                    </FormHelperText>
                                    {this.state.errors &&
                                    this.state.errors.language ? (
                                        <FormHelperText error>
                                            {this.state.errors.language}
                                        </FormHelperText>
                                    ) : null}
                                </FormControl>
                            </TabPanel>
                            <TabPanel
                                value={this.state.activeTab}
                                index="templates"
                            >
                                <Typography variant="h5" gutterBottom>
                                    {kedo.t('Templates')}
                                </Typography>
                                {this.renderTemplates()}
                            </TabPanel>
                            <TabPanel value={this.state.activeTab} index="misc">
                                <Typography variant="h5" gutterBottom>
                                    {kedo.t('Misc')}
                                </Typography>
                            </TabPanel>
                        </Grid>
                    </Grid>
                </div>
            </AppDialog>
        )
    }

    renderApply(
        templId: number | string,
        templates: Template[],
        environment: any
    ) {
        const templ = templates.find((item) => item.id === templId)
        if (!templ || templ.template) {
            return <p>{this.props.kedo.t('Template could not be applied')}</p>
        }

        return (
            <Applying
                confirmed={true}
                popup={true}
                kedo={this.props.kedo}
                environment={environment}
                templateName={templ.name}
                template={JSON.parse(templ.template!)}
            />
        )
    }

    componentDidUpdate(
        prevProps: NewEnvironmentProps,
        prevState: NewEnvironmentState
    ) {
        if (
            prevState.addLocales !== this.state.addLocales ||
            prevState.description !== this.state.description ||
            prevState.file !== this.state.file ||
            prevState.imagePreviewUrl !== this.state.imagePreviewUrl ||
            prevState.name !== this.state.name ||
            prevState.template !== this.state.template
        ) {
            this.setState({ unsavedChanges: true })
        }
    }

    componentDidMount() {
        this._isMounted = true
        this.fetchTemplates()
        this.fetchLocales()
    }

    componentWillUnmount() {
        this._isMounted = false
    }

    render() {
        if (true !== this.props.kedo.isAdminOrContractAdmin()) {
            return ''
        }

        return (
            <div
                className={'newEnvironmentButton'}
                style={{ marginRight: '10px' }}
            >
                {this.state.showConfirmChanges
                    ? this.renderRemoveChangesForm()
                    : null}
                {this.state.runTemplate ? (
                    <Applying
                        kedo={this.props.kedo}
                        manualConfirm={true}
                        onClose={this.handleExit}
                        popup={true}
                        environment={this.state.newEnvironment}
                        template={JSON.parse(
                            this.state.templates.find(
                                (item) => item.id === this.state.template
                            )!.template!
                        )}
                    />
                ) : null}
                {!this.state.runTemplate ? this.newEnvironmentForm() : null}
            </div>
        )
    }
}

export default NewEnvironment
